/**
 * Utilidades compartidas para formatear puntajes de actividades.
 *
 * Se usa tanto en el PDF de resultados como en la UI del ranking para que el
 * director vea exactamente la misma información que sale impresa.
 */

import type {
  ActivityScoreConfig,
  ActivityScoreInput,
  ActivityScoreMode,
} from "@/types";

export const SCORE_MODE_LABEL: Record<ActivityScoreMode, string> = {
  podium: "Podio (1°/2°/3°/Participación)",
  pass_fail: "Cumple / No cumple",
  fixed: "Entregó / No entregó",
  timing: "Por fecha de entrega",
  deductive: "Deductivo (base − faltas)",
  accumulative: "Acumulativo (base + unidades − penalización)",
};

export const SCORE_MODE_DESCRIPTION: Record<ActivityScoreMode, string> = {
  podium:
    "Se otorga puntaje según el lugar obtenido (1°, 2°, 3° o Participación). Varios clubes pueden compartir el mismo lugar si hay empate.",
  pass_fail:
    "Se otorgan puntos completos si el club cumple con el requisito; cero puntos en caso contrario.",
  fixed:
    "Se otorgan puntos completos si el club entrega lo requerido; cero puntos si no lo entrega.",
  timing:
    "Los puntos dependen de la fecha en la que el club entregó el requisito. Se elige la banda correspondiente.",
  deductive:
    "Se parte de un puntaje base y se restan puntos por cada falta registrada por el jurado.",
  accumulative:
    "Se parte de un puntaje base y se suman puntos por cada unidad completada. Se restan puntos por penalizaciones.",
};

/**
 * Texto legible del resultado capturado por el jurado para una actividad.
 *
 * Ejemplos:
 *   podium → "1° lugar" / "Participación"
 *   pass_fail → "Cumple" / "No cumple"
 *   timing → nombre de la banda
 *   deductive → "Faltas: N"
 *   accumulative → "N unidades / P penalizaciones"
 */
export function formatActivityResult(
  mode: ActivityScoreMode,
  config: ActivityScoreConfig | undefined,
  input: ActivityScoreInput | undefined,
): string {
  if (!input) return "Sin registro";
  const cfg = config ?? ({} as ActivityScoreConfig);
  switch (mode) {
    case "podium": {
      switch (input.position) {
        case "first":
          return "1° lugar";
        case "second":
          return "2° lugar";
        case "third":
          return "3° lugar";
        case "participation":
          return "Participación";
        default:
          return "Sin registro";
      }
    }
    case "pass_fail":
      if (input.passed === true) return "Cumple";
      if (input.passed === false) return "No cumple";
      return "Sin registro";
    case "fixed":
      if (input.delivered === true) return "Entregó";
      if (input.delivered === false) return "No entregó";
      return "Sin registro";
    case "timing": {
      const idx = input.timingBandIndex;
      if (idx === undefined || idx === null) return "Sin registro";
      const bands = cfg.timingBands ?? [];
      return bands[idx]?.label || `Banda ${idx + 1}`;
    }
    case "deductive": {
      if (input.deductionPoints === undefined) return "Sin registro";
      return `${input.deductionPoints} falta(s)`;
    }
    case "accumulative": {
      const units = input.unitsCompleted;
      const pen = input.penaltyUnits;
      if (units === undefined && pen === undefined) return "Sin registro";
      return `${units ?? 0} unid. / ${pen ?? 0} penal.`;
    }
    default:
      return "Sin registro";
  }
}

/**
 * Resumen legible de la rúbrica (cómo se calculan los puntos) para mostrar
 * como leyenda al lado del puntaje obtenido.
 */
export function formatActivityRubric(
  mode: ActivityScoreMode,
  config: ActivityScoreConfig | undefined,
): string {
  const cfg = config ?? ({} as ActivityScoreConfig);
  const n = (v: number | undefined) => (v ?? 0).toLocaleString("es-CO");
  switch (mode) {
    case "podium": {
      const p = cfg.podium ?? {
        first: 0,
        second: 0,
        third: 0,
        participation: 0,
      };
      return `1° = ${n(p.first)} · 2° = ${n(p.second)} · 3° = ${n(p.third)} · Participación = ${n(p.participation)}`;
    }
    case "pass_fail":
      return `Cumple = ${n(cfg.passFailValue)} · No cumple = 0`;
    case "fixed":
      return `Entregó = ${n(cfg.fixedValue)} · No entregó = 0`;
    case "timing":
      return (cfg.timingBands ?? [])
        .map((b) => `${b.label} = ${n(b.points)}`)
        .join(" · ");
    case "deductive":
      return `Base = ${n(cfg.deductiveBase)} − puntos por cada falta`;
    case "accumulative":
      return `Base = ${n(cfg.accumulativeBase)} + ${n(cfg.accumulativePerUnit)}/unidad − ${n(cfg.accumulativePenaltyPerUnit)}/penalización`;
    default:
      return "";
  }
}
